import { useCallback } from "react";
import { atom, PrimitiveAtom, useAtom } from "jotai";

import { Api } from "interfaces";

import { useGetTypesForFile } from "modules/data-files/api";
import { useCreateProjectWithDataAndWorkspace } from "modules/projects/api";

import { ProjectMetaData } from "flows/impact-analysis-binary/interfaces";
import * as flowConfig from "flows/impact-analysis-binary/config";

import {
  useGetInputFile,
  useGetTimeColumn,
  useGetYDefinition,
  useGetYZerosTag,
  useGetYOnesTag,
  useGetIDVariables,
  useGetTreatmentDefinition,
  useGetTreatmentTag,
  useGetControlTag,
} from "./store";

const projectCreateStatus = atom<"not-started" | "in-progress" | "completed">(
  "not-started"
);

const projectCreateError = atom<string | Api.ApiError>(null) as PrimitiveAtom<
  string | Api.ApiError
>;

export const useGetCreate = () => {
  const createProject = useCreateProjectWithDataAndWorkspace();

  const inputFile = useGetInputFile();
  const colTypes = useGetTypesForFile(inputFile);

  const yDefinition = useGetYDefinition();
  const yZerosTag = useGetYZerosTag();
  const yOnesTag = useGetYOnesTag();

  const treatmentDefinition = useGetTreatmentDefinition();
  const treatmentTag = useGetTreatmentTag();
  const controlTag = useGetControlTag();

  const timeColumn = useGetTimeColumn();
  const idColumns = useGetIDVariables();

  const [status, setStatus] = useAtom(projectCreateStatus);
  const [error, setError] = useAtom(projectCreateError);

  const create = useCallback(async () => {
    if (
      !inputFile ||
      yDefinition.length === 0 ||
      treatmentDefinition.length === 0
    ) {
      setError(
        "Input Data, Y Definitions, and Treatment Definitions ARE ALL MANDATORY!!!"
      );
      setStatus("completed");
      return;
    }
    setStatus("in-progress");
    setError(null);
    const projectMetaData: ProjectMetaData = {
      [flowConfig.Y_FILTERS_KEY]: yDefinition,
      [flowConfig.Y_TAG_ZEROS]: yZerosTag,
      [flowConfig.Y_TAG_ONES]: yOnesTag,
      [flowConfig.TREATMENT_FILTERS_KEY]: treatmentDefinition,
      [flowConfig.TREATMENT_TAG]: treatmentTag,
      [flowConfig.CONTROL_TAG]: controlTag,
    };
    const projectSpec = {
      flow: flowConfig.FLOW_NAME,
      data_files: { [flowConfig.INPUT_DATA]: inputFile },
      meta_data: projectMetaData,
    };

    const wsColTypes = { ...colTypes };
    if (timeColumn && wsColTypes[timeColumn] === "char") {
      wsColTypes[timeColumn] = "date";
    }

    const workspaceSpec = {
      key: "init",
      recipe: flowConfig.RECIPE,
      annotations: {
        [flowConfig.RUN_ID]: "INIT",
        [flowConfig.TIME_STAMP]: new Date().valueOf(),
      },
      input_data: { [flowConfig.INPUT_DATA]: [inputFile] },
      cleanup_spec: {
        [inputFile]: {
          column_types: wsColTypes,
        },
      },
      variable_mapping: {
        [flowConfig.TIME_VARIABLE_KEY]: timeColumn ? [timeColumn] : [],
        [flowConfig.ID_VARIABLES_KEY]: idColumns || [],
      },
      model_params: {
        [flowConfig.Y_FILTERS_KEY]: yDefinition,
        [flowConfig.TREATMENT_FILTERS_KEY]: treatmentDefinition,
      },
    };

    const resp = await createProject({
      "project-spec": projectSpec,
      "workspace-spec": workspaceSpec,
    });

    if (resp.success) {
    } else if (resp.success === false) {
      setError(resp.error);
    }
    setStatus("completed");
    return resp;
  }, [
    inputFile,
    treatmentDefinition,
    treatmentTag,
    controlTag,
    timeColumn,
    yDefinition,
    yOnesTag,
    yZerosTag,
    idColumns,
    colTypes,
  ]);

  return [create, status, error] as const;
};
